import React, { useState, useRef, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { SpecialForYouItem } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { formatRupiah } from '../../utils/formatters';
import {
  Sparkles,
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Save,
  Plus,
  Loader2,
  Check,
  Eye,
  EyeOff,
  Edit2,
  X,
  AlertCircle,
  Lock,
  Calendar,
  Tag,
  ExternalLink,
} from 'lucide-react';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB existing project limit

export const SpecialForYouManager: React.FC = () => {
  const { data, auth, updateData, saveData, uploadImage, showToast } = useContent();

  const isSuperAdmin = auth.role !== 'outlet_admin';

  const [items, setItems] = useState<SpecialForYouItem[]>(() => {
    const raw = Array.isArray(data.specialForYou) ? data.specialForYou : [];
    return [...raw].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showLivePreview, setShowLivePreview] = useState<boolean>(true);

  // Modal / Form state for Add / Edit
  const [editingItem, setEditingItem] = useState<SpecialForYouItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formTitle, setFormTitle] = useState<string>('');
  const [formDescription, setFormDescription] = useState<string>('');
  const [formImageUrl, setFormImageUrl] = useState<string>('');
  const [formPrice, setFormPrice] = useState<number>(0);
  const [formDisplayOrder, setFormDisplayOrder] = useState<number>(1);
  const [formIsActive, setFormIsActive] = useState<boolean>(true);
  const [formDiscountInfo, setFormDiscountInfo] = useState<string>('');
  const [formTerms, setFormTerms] = useState<string>('');
  const [formStartDate, setFormStartDate] = useState<string>('');
  const [formEndDate, setFormEndDate] = useState<string>('');
  const [formBadgeText, setFormBadgeText] = useState<string>('');
  const [formCtaText, setFormCtaText] = useState<string>('PESAN SEKARANG');
  const [formProductId, setFormProductId] = useState<string>('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync local state when context data updates
  useEffect(() => {
    const raw = Array.isArray(data.specialForYou) ? data.specialForYou : [];
    setItems([...raw].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)));
  }, [data.specialForYou]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8 rounded-2xl bg-slate-900 border border-slate-800 text-center max-w-lg mx-auto my-12 space-y-3">
        <div className="w-12 h-12 rounded-2xl bg-rose-950/60 border border-rose-800/50 text-rose-400 flex items-center justify-center mx-auto">
          <Lock className="w-6 h-6" />
        </div>
        <h3 className="font-display font-bold text-lg text-white uppercase">
          Akses Terbatas — Khusus Admin Pusat
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed">
          Section CMS &ldquo;Promo Untukmu&rdquo; bersifat global dan hanya dapat dikelola oleh Admin Pusat / Super Admin.
        </p>
      </div>
    );
  }

  const persistItems = async (newList: SpecialForYouItem[], notify = false): Promise<boolean> => {
    setIsSaving(true);
    try {
      const sorted = [...newList].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
      let success = false;
      if (typeof updateData === 'function') {
        success = await updateData({
          specialForYou: sorted,
        });
      } else if (typeof saveData === 'function') {
        success = await saveData({
          ...data,
          specialForYou: sorted,
        });
      }

      if (notify) {
        if (success) {
          showToast('Data "Promo Untukmu" berhasil disimpan ke database!', 'success');
        } else {
          showToast('Tersimpan di penyimpanan lokal.', 'info');
        }
      }
      return success;
    } catch (err: any) {
      console.error('[CMS Promo Error]:', err);
      showToast('Gagal menyimpan perubahan ke database.', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddModal = () => {
    const nextOrder =
      items.length > 0 ? Math.max(...items.map((item) => item.display_order || 0)) + 1 : 1;
    const defaultMenu = Array.isArray(data.menuItems) && data.menuItems.length > 0 ? data.menuItems[0] : null;
    setEditingItem(null);
    setFormTitle('');
    setFormDescription('');
    setFormImageUrl('');
    setFormProductId(defaultMenu?.id || '');
    setFormPrice(defaultMenu?.price ?? 25000);
    setFormDisplayOrder(nextOrder);
    setFormIsActive(true);
    setFormDiscountInfo('');
    setFormTerms('');
    setFormStartDate('');
    setFormEndDate('');
    setFormBadgeText('');
    setFormCtaText('PESAN SEKARANG');
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: SpecialForYouItem) => {
    setEditingItem(item);
    setFormTitle(item.title || '');
    setFormDescription(item.description || '');
    setFormImageUrl(item.image_url || '');
    setFormProductId(item.product_id || (Array.isArray(data.menuItems) && data.menuItems[0]?.id) || '');
    setFormPrice(Number(item.price ?? 0));
    setFormDisplayOrder(item.display_order || 1);
    setFormIsActive(item.is_active !== false);
    setFormDiscountInfo(item.discount_info || '');
    setFormTerms(item.terms_and_conditions || '');
    setFormStartDate(item.start_date || '');
    setFormEndDate(item.end_date || '');
    setFormBadgeText(item.badge_text || '');
    setFormCtaText(item.cta_text || 'PESAN SEKARANG');
    setUploadError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setUploadError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const mimeLower = (file.type || '').toLowerCase();
    const extLower = (file.name.split('.').pop() || '').toLowerCase();
    const isAllowedExt = ['jpg', 'jpeg', 'png', 'webp'].includes(extLower);

    if (!ALLOWED_IMAGE_TYPES.includes(mimeLower) && !isAllowedExt) {
      setUploadError('Format file tidak didukung. Gunakan JPG, PNG, atau WEBP.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError('Ukuran file terlalu besar. Maksimal 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadedUrl = await uploadImage(file, 'special_for_you');
      if (uploadedUrl) {
        setFormImageUrl(uploadedUrl);
        showToast('Foto Promo berhasil diupload!', 'success');
      } else {
        setUploadError('Gagal mengupload foto. Silakan coba lagi.');
      }
    } catch (err: any) {
      setUploadError('Terjadi kesalahan saat upload foto: ' + (err?.message || 'Error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveModalForm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTitle.trim()) {
      setUploadError('Nama / judul promo wajib diisi.');
      return;
    }

    if (!formImageUrl.trim()) {
      setUploadError('Foto / banner promo wajib diupload.');
      return;
    }

    const nowIso = new Date().toISOString();
    let updatedList: SpecialForYouItem[];

    if (editingItem) {
      updatedList = items.map((item) => {
        if (item.id === editingItem.id) {
          return {
            ...item,
            product_id: formProductId.trim() || item.product_id || undefined,
            title: formTitle.trim(),
            description: formDescription.trim(),
            image_url: formImageUrl.trim(),
            price: Math.max(0, Number(formPrice) || 0),
            display_order: Math.max(1, Number(formDisplayOrder) || 1),
            is_active: formIsActive,
            discount_info: formDiscountInfo.trim(),
            terms_and_conditions: formTerms.trim(),
            start_date: formStartDate.trim(),
            end_date: formEndDate.trim(),
            badge_text: formBadgeText.trim(),
            cta_text: formCtaText.trim() || 'PESAN SEKARANG',
            updated_at: nowIso,
          };
        }
        return item;
      });
    } else {
      const newItem: SpecialForYouItem = {
        id: `promo-${Date.now()}`,
        product_id: formProductId.trim() || undefined,
        title: formTitle.trim(),
        description: formDescription.trim(),
        image_url: formImageUrl.trim(),
        price: Math.max(0, Number(formPrice) || 0),
        display_order: Math.max(1, Number(formDisplayOrder) || 1),
        is_active: formIsActive,
        discount_info: formDiscountInfo.trim(),
        terms_and_conditions: formTerms.trim(),
        start_date: formStartDate.trim(),
        end_date: formEndDate.trim(),
        badge_text: formBadgeText.trim(),
        cta_text: formCtaText.trim() || 'PESAN SEKARANG',
        created_at: nowIso,
        updated_at: nowIso,
      };
      updatedList = [...items, newItem];
    }

    updatedList.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

    setItems(updatedList);
    persistItems(updatedList, true);
    handleCloseModal();
  };

  const handleToggleActive = (id: string) => {
    const nowIso = new Date().toISOString();
    const updated = items.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          is_active: !item.is_active,
          updated_at: nowIso,
        };
      }
      return item;
    });
    setItems(updated);
    persistItems(updated, false);
  };

  const handleConfirmDelete = (id: string) => {
    const updated = items.filter((item) => item.id !== id);
    setItems(updated);
    setPendingDeleteId(null);
    persistItems(updated, true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const list = [...items];
    const temp = list[index];
    list[index] = list[index - 1];
    list[index - 1] = temp;

    const nowIso = new Date().toISOString();
    const reordered = list.map((item, idx) => ({
      ...item,
      display_order: idx + 1,
      updated_at: nowIso,
    }));

    setItems(reordered);
    persistItems(reordered, false);
  };

  const handleMoveDown = (index: number) => {
    if (index >= items.length - 1) return;
    const list = [...items];
    const temp = list[index];
    list[index] = list[index + 1];
    list[index + 1] = temp;

    const nowIso = new Date().toISOString();
    const reordered = list.map((item, idx) => ({
      ...item,
      display_order: idx + 1,
      updated_at: nowIso,
    }));

    setItems(reordered);
    persistItems(reordered, false);
  };

  const activeItemsPreview = items
    .filter((item) => item.is_active === true)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 text-[#00E5FF] flex items-center justify-center border border-[#00E5FF]/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <h2 className="font-display font-black text-2xl text-white uppercase tracking-tight">
              CMS PROMO UNTUKMU
            </h2>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Kelola promo spesial, diskon, banner, periode tayang, dan syarat ketentuan lengkap dengan halaman detail promo.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => persistItems(items, true)}
            disabled={isSaving}
            className="px-5 py-2.5 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 font-display font-bold text-xs tracking-wider uppercase flex items-center gap-2 shadow-lg shadow-[#00E5FF]/20 cursor-pointer disabled:opacity-50 transition-all active:scale-95"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{isSaving ? 'Menyimpan...' : 'SIMPAN KE DATABASE'}</span>
          </button>
        </div>
      </div>

      {/* Live Preview Card */}
      <div className="p-5 sm:p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00E5FF]" />
            <h3 className="font-display font-bold text-sm text-white uppercase tracking-wider">
              Live Preview Homepage ({activeItemsPreview.length} Promo Aktif)
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setShowLivePreview(!showLivePreview)}
            className="text-xs font-mono text-slate-400 hover:text-[#00E5FF] flex items-center gap-1.5 cursor-pointer"
          >
            {showLivePreview ? (
              <EyeOff className="w-3.5 h-3.5" />
            ) : (
              <Eye className="w-3.5 h-3.5 text-[#00E5FF]" />
            )}
            <span>{showLivePreview ? 'Sembunyikan' : 'Tampilkan'}</span>
          </button>
        </div>

        {showLivePreview && (
          <div className="pt-2">
            {activeItemsPreview.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                {activeItemsPreview.map((item) => (
                  <div
                    key={`preview-${item.id}`}
                    className="shrink-0 w-60 sm:w-64 bg-white rounded-2xl p-3 border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(15,30,54,0.05)] flex flex-col justify-between"
                  >
                    <div>
                      <div className="relative h-28 rounded-xl overflow-hidden mb-2.5 bg-slate-100">
                        <img
                          src={resolveMediaUrl(item.image_url)}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                        {item.badge_text && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#0056D2] text-white shadow-xs">
                            {item.badge_text}
                          </span>
                        )}
                      </div>
                      <h4 className="font-bold text-xs sm:text-sm text-[#0F1E36] leading-snug line-clamp-1">
                        {item.title}
                      </h4>
                      <p className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1">
                        {item.description || 'Promo spesial LetonKu'}
                      </p>
                    </div>

                    <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between">
                      {item.discount_info ? (
                        <span className="text-[10px] font-bold text-[#0056D2] truncate max-w-[130px]">
                          {item.discount_info}
                        </span>
                      ) : (
                        <span className="text-xs font-extrabold text-[#1E3A8A] font-mono tabular-nums">
                          {formatRupiah(item.price)}
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-[#EBF2FF] text-[#0056D2] text-[10px] font-bold rounded-lg">
                        Detail →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 rounded-2xl bg-slate-950/80 border border-dashed border-slate-800 text-center text-slate-500 text-xs">
                Belum ada promo yang aktif. Homepage tidak akan menampilkan kartu pada section ini.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Items List Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <h3 className="font-display font-bold text-base text-white uppercase tracking-wider">
              Daftar Promo ({items.length})
            </h3>
            <p className="text-xs text-slate-400">
              Gunakan tombol panah untuk mengatur urutan promo di layar beranda.
            </p>
          </div>
          <button
            type="button"
            onClick={handleOpenAddModal}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-[#00E5FF] hover:text-white font-mono font-bold text-xs border border-slate-700 flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Tambah Promo Baru</span>
          </button>
        </div>

        {items.length === 0 ? (
          <div className="p-12 rounded-2xl bg-slate-900 border border-slate-800 text-center space-y-3">
            <Sparkles className="w-8 h-8 text-slate-600 mx-auto" />
            <h4 className="font-bold text-white text-sm">Belum Ada Promo</h4>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              Mulai tambahkan promo menarik dengan banner, deskripsi lengkap, keuntungan diskon, dan periode tayang.
            </p>
            <button
              type="button"
              onClick={handleOpenAddModal}
              className="mt-2 px-4 py-2 rounded-xl bg-[#00E5FF] text-slate-950 font-bold text-xs uppercase cursor-pointer"
            >
              Tambah Promo Pertama
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === items.length - 1;

              return (
                <div
                  key={item.id}
                  className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    item.is_active
                      ? 'bg-slate-900/90 border-slate-800'
                      : 'bg-slate-950/60 border-slate-900 opacity-60'
                  }`}
                >
                  {/* Left: Thumbnail & Details */}
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-16 h-12 rounded-xl overflow-hidden bg-slate-950 border border-slate-800 shrink-0">
                      <img
                        src={resolveMediaUrl(item.image_url)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-sm text-white truncate">
                          {item.title}
                        </h4>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase ${
                            item.is_active
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                              : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {item.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                        {item.badge_text && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-900/60 text-blue-300 border border-blue-700/50">
                            {item.badge_text}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-xs mt-0.5 flex-wrap">
                        <span className="font-mono font-bold text-[#00E5FF]">
                          {formatRupiah(item.price)}
                        </span>
                        {item.discount_info && (
                          <span className="text-slate-300 text-[11px] flex items-center gap-1">
                            <Tag className="w-3 h-3 text-[#00E5FF]" />
                            {item.discount_info}
                          </span>
                        )}
                        {(item.start_date || item.end_date) && (
                          <span className="text-slate-400 text-[11px] flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            {item.start_date} - {item.end_date}
                          </span>
                        )}
                      </div>

                      {item.description && (
                        <p className="text-xs text-slate-400 truncate mt-0.5">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                    {/* View Promo Page */}
                    <a
                      href={`#promo?id=${encodeURIComponent(item.id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-[#00E5FF] transition-colors cursor-pointer border border-slate-700"
                      title="Buka Halaman Detail Promo"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>

                    {/* Reorder Arrows */}
                    <div className="flex items-center bg-slate-950 rounded-lg p-0.5 border border-slate-800">
                      <button
                        type="button"
                        disabled={isFirst}
                        onClick={() => handleMoveUp(index)}
                        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        title="Geser ke Atas"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={isLast}
                        onClick={() => handleMoveDown(index)}
                        className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                        title="Geser ke Bawah"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Toggle Active */}
                    <button
                      type="button"
                      onClick={() => handleToggleActive(item.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono font-bold transition-colors cursor-pointer border ${
                        item.is_active
                          ? 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                          : 'bg-emerald-950/40 hover:bg-emerald-900/40 text-emerald-400 border-emerald-800/50'
                      }`}
                    >
                      {item.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                    </button>

                    {/* Edit Button */}
                    <button
                      type="button"
                      onClick={() => handleOpenEditModal(item)}
                      className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer border border-slate-700"
                      title="Edit Item"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>

                    {/* Delete Confirmation / Button */}
                    {pendingDeleteId === item.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleConfirmDelete(item.id)}
                          className="px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-bold cursor-pointer"
                        >
                          Ya, Hapus
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDeleteId(null)}
                          className="px-2 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-[11px] cursor-pointer"
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(item.id)}
                        className="p-2 rounded-lg bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 transition-colors cursor-pointer border border-rose-800/40"
                        title="Hapus Item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Form for Add / Edit Promo Item */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-xl w-full p-6 shadow-2xl space-y-5 text-white my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#00E5FF]" />
                <h3 className="font-display font-bold text-base uppercase">
                  {editingItem ? 'Edit Data Promo' : 'Tambah Promo Baru'}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1 rounded-lg text-slate-400 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveModalForm} className="space-y-4">
              {uploadError && (
                <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-800 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                  <span>{uploadError}</span>
                </div>
              )}

              {/* Image Upload Area */}
              <div className="space-y-2">
                <label className="block text-xs font-mono uppercase text-slate-300 font-semibold">
                  Banner / Foto Promo <span className="text-rose-400">*</span>
                </label>

                <div className="aspect-[16/9] rounded-2xl bg-slate-950 border border-slate-800 overflow-hidden relative flex flex-col items-center justify-center p-4">
                  {formImageUrl ? (
                    <>
                      <img
                        src={resolveMediaUrl(formImageUrl)}
                        alt="Preview Banner Promo"
                        className="w-full h-full object-cover rounded-xl"
                        referrerPolicy="no-referrer"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-slate-900/90 hover:bg-slate-900 text-white text-xs font-mono border border-slate-700 shadow-md flex items-center gap-1.5 cursor-pointer"
                      >
                        <Upload className="w-3.5 h-3.5 text-[#00E5FF]" />
                        <span>{isUploading ? 'Mengunggah...' : 'Ganti Foto'}</span>
                      </button>
                    </>
                  ) : (
                    <div className="text-center space-y-2">
                      <div className="w-10 h-10 rounded-xl bg-slate-900 text-[#00E5FF] flex items-center justify-center mx-auto">
                        <Upload className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">Upload Banner Promo</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          Format JPG, PNG, atau WEBP (Maks. 10MB)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="px-4 py-2 rounded-xl bg-[#00E5FF] text-slate-950 font-bold text-xs uppercase cursor-pointer"
                      >
                        {isUploading ? 'Mengunggah...' : 'Pilih Gambar'}
                      </button>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Title, Badge & Price */}
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Nama / Judul Promo <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      placeholder="Contoh: Paket Senja Kopi & Croissant"
                      required
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Badge Label
                    </label>
                    <input
                      type="text"
                      value={formBadgeText}
                      onChange={(e) => setFormBadgeText(e.target.value)}
                      placeholder="Contoh: HEMAT 25%"
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>
                </div>

                {/* Menu Item Reference Selector */}
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Produk Menu Terkait (Referensi Checkout) <span className="text-rose-400">*</span>
                  </label>
                  <select
                    value={formProductId}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setFormProductId(nextId);
                      const matchedMenu = (data.menuItems || []).find((m) => m.id === nextId);
                      if (matchedMenu && typeof matchedMenu.price === 'number') {
                        setFormPrice(matchedMenu.price);
                      }
                    }}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  >
                    {(data.menuItems || []).map((menu) => (
                      <option key={menu.id} value={menu.id}>
                        {menu.name} — {formatRupiah(menu.price)} ({menu.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Harga / Nilai Katalog (Rp)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="500"
                      value={formPrice}
                      onChange={(e) => setFormPrice(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Informasi Diskon / Benefit
                    </label>
                    <input
                      type="text"
                      value={formDiscountInfo}
                      onChange={(e) => setFormDiscountInfo(e.target.value)}
                      placeholder="Contoh: Hemat 25% + Bonus 50 Poin"
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>
                </div>

                {/* Period Dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Tanggal Mulai
                    </label>
                    <input
                      type="text"
                      value={formStartDate}
                      onChange={(e) => setFormStartDate(e.target.value)}
                      placeholder="Contoh: 1 Nov 2025"
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                      Tanggal Berakhir
                    </label>
                    <input
                      type="text"
                      value={formEndDate}
                      onChange={(e) => setFormEndDate(e.target.value)}
                      placeholder="Contoh: 31 Des 2025"
                      className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                    />
                  </div>
                </div>

                {/* Full Description */}
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Deskripsi Lengkap
                  </label>
                  <textarea
                    rows={3}
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Jelaskan detail menu promo, cita rasa, dan pengalaman yang didapatkan..."
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                {/* Terms and Conditions */}
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Syarat & Ketentuan (Pisahkan dengan baris baru)
                  </label>
                  <textarea
                    rows={4}
                    value={formTerms}
                    onChange={(e) => setFormTerms(e.target.value)}
                    placeholder={`• Berlaku untuk Dine In, Take Away, dan Delivery\n• Minimal transaksi Rp 30.000\n• Berlaku setiap hari selama jam operasional`}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs font-mono focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                {/* CTA Button Text */}
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Teks Tombol Aksi
                  </label>
                  <input
                    type="text"
                    value={formCtaText}
                    onChange={(e) => setFormCtaText(e.target.value)}
                    placeholder="PESAN SEKARANG"
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>
              </div>

              {/* Display Order & Active Toggle */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Urutan Tampil
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formDisplayOrder}
                    onChange={(e) => setFormDisplayOrder(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-[#00E5FF]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase text-slate-300 font-semibold mb-1">
                    Status Tayang
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormIsActive(!formIsActive)}
                    className={`w-full py-2 px-3 rounded-xl text-xs font-bold font-mono transition-colors flex items-center justify-center gap-2 cursor-pointer border ${
                      formIsActive
                        ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-400'
                        : 'bg-slate-950 border-slate-800 text-slate-400'
                    }`}
                  >
                    {formIsActive ? <Check className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <span>{formIsActive ? 'Aktif di Homepage' : 'Nonaktif'}</span>
                  </button>
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isUploading || !formImageUrl.trim() || !formTitle.trim()}
                  className="px-6 py-2 rounded-xl bg-[#00E5FF] hover:bg-[#3cf0ff] text-slate-950 font-bold text-xs uppercase cursor-pointer disabled:opacity-50"
                >
                  {editingItem ? 'Simpan Perubahan' : 'Tambahkan Promo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
